import React, { useState, useEffect, useRef, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import { useAppSelector } from "../app/hooks";
import { selectAircraftVitalsById } from "../features/aircraft/aircraftSelectors";
import { useInterval } from "../useInterval";

interface MissionTimerProps {
  uavId: string;
}

export const MissionTimer: React.FC<MissionTimerProps> = ({
  uavId,
}) => {
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [mode, setMode] = useState<"countdown" | "countup">("countdown");
  const [isInitialized, setIsInitialized] = useState(false);
  const prevSubStateRef = useRef<string | undefined>(undefined);
  
  const vitals = useAppSelector((state) =>
    uavId ? selectAircraftVitalsById(state, uavId) : null
  );
  
  const initialTimerState = useMemo(() => {
    if (!vitals?.departureTime) return null;
    
    try {
      const currentTimeMs = Date.now();
      let departureTimeMs = vitals.departureTime;

      if (isNaN(departureTimeMs) || !isFinite(departureTimeMs)) {
        console.error("Invalid departure time value");
        return {
          seconds: 0,
          mode: "countup" as const
        };
      }

      const diffSeconds = Math.round((departureTimeMs - currentTimeMs) / 1000);

      if (diffSeconds > 0) {
        return {
          seconds: diffSeconds,
          mode: "countdown" as const
        };
      } else {
        return {
          seconds: Math.abs(diffSeconds),
          mode: "countup" as const
        };
      }
    } catch (error) {
      console.error("Error processing departure time:", error);
      return {
        seconds: 0,
        mode: "countup" as const
      };
    }
  }, [vitals?.departureTime]);
  
  useEffect(() => {
    if (initialTimerState && !isInitialized) {
      setSecondsElapsed(initialTimerState.seconds);
      setMode(initialTimerState.mode);
      setIsInitialized(true);
      console.log(`Starting ${initialTimerState.mode} at ${initialTimerState.seconds} seconds`);
    }
  }, [initialTimerState, isInitialized]);
  
  useEffect(() => {
    if (!vitals?.uavSubState) return;

    const currentSubState = vitals.uavSubState;
    
    if (currentSubState.includes("STATE_FLIGHT_MISSION") && prevSubStateRef.current &&
        !prevSubStateRef.current.includes("STATE_FLIGHT_MISSION")) {
      setMode("countup");
      
      if (mode === "countdown") {
        setSecondsElapsed(0);
      }
    }

    prevSubStateRef.current = currentSubState;
  }, [vitals?.uavSubState, mode]);

  useInterval(() => {
    setSecondsElapsed((prevSeconds) => {
      if (mode === "countdown" && prevSeconds > 0) {
        return prevSeconds - 1;
      } else if (mode === "countup" || (mode === "countdown" && prevSeconds <= 0)) {
        if (mode === "countdown" && prevSeconds <= 0) {
          setMode("countup");
          return 1;
        }
        return prevSeconds + 1;
      } else {
        return 0;
      }
    });
  }, 1000);

  const formatTime = (seconds: number) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
      seconds = 0;
    }
    
    const cappedSeconds = Math.min(seconds, 5999);
    
    const minutes = Math.floor(cappedSeconds / 60);
    const remainingSeconds = cappedSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <Typography
      display={"flex"}
      width={"100%"}
      justifyContent={"center"}
      sx={{
        fontFamily: "monospace",
        color: "#FFFFFF",
        fontWeight: "600",
        fontSize: 20,
        textAlign: "center",
        whiteSpace: "nowrap",
        padding: '2px 0',
      }}
    >
      {mode === "countdown" && secondsElapsed > 0 ? (
        <span style={{ fontSize: '14px', marginRight: '2px' }}>T-</span>
      ) : ""}
      {formatTime(secondsElapsed)}
    </Typography>
  );
};